import { Controller } from '@hotwired/stimulus';
import { pluralize } from '../lib/i18n';
import { paint, typing } from '../lib/dom';
import { chord } from '../lib/keys';
import History from '../lib/workflow/history';
import serialize from '../lib/workflow/fields';
import validate from '../lib/workflow/validation';
import * as graph from '../lib/workflow/graph';
import { copyPayload, parsePayload, pasteNodes } from '../lib/workflow/clipboard';
import { stepDetail, stepTitle, triggerFace } from '../lib/workflow/presenter';

const STEP_DEFAULTS = {
  key: '', command: '', provider: '', credentialId: '', prompt: '', conditions: '', instructions: '',
  interruptible: false,
  childWorkflowId: '', childInputs: '', each: '', allowFailures: false,
  canvasX: null, canvasY: null,
};
const TRIGGER_DEFAULTS = { name: '', webhookId: '', events: '', conditions: '', cron: '' };

export default class extends Controller {
  static targets = ['dirty', 'discard', 'fields', 'leaveModal', 'panel', 'save', 'summary', 'tab', 'title'];

  static values = {
    bundleUrl: String,
    bundles: Array,
    credentials: Array,
    revisions: Object,
    labels: Object,
    mcpServers: Array,
    messages: Object,
    operators: Array,
    shortcuts: Array,
    state: Object,
    stepOperators: Array,
    tones: Object,
    webhooks: Array,
    workflows: Array,
  };

  static classes = ['tabActive', 'tabIdle'];

  static outlets = ['workflow-canvas', 'workflow-json', 'workflow-settings', 'workflow-steps'];

  get #canvas() {
    return this.hasWorkflowCanvasOutlet ? this.workflowCanvasOutlet : null;
  }

  get #json() {
    return this.hasWorkflowJsonOutlet ? this.workflowJsonOutlet : null;
  }

  workflowCanvasOutletConnected(canvas) {
    canvas.render(this.#scene());
  }

  get #settings() {
    return this.hasWorkflowSettingsOutlet ? this.workflowSettingsOutlet : null;
  }

  workflowSettingsOutletConnected(settings) {
    if (this.working) settings.render(this.working, this.errors);
  }

  get #steps() {
    return this.hasWorkflowStepsOutlet ? this.workflowStepsOutlet : null;
  }

  workflowStepsOutletConnected(steps) {
    if (this.working) steps.render(this.#outline());
  }

  workflowJsonOutletConnected(json) {
    json.sync(this.working);
    json.showStatus(this.documentError);
  }

  connect() {
    this.reset = () => this.#reset();
    this.warnBeforeUnload = (event) => {
      if (this.#dirty()) event.preventDefault();
    };

    this.handleKey = (event) => this.#shortcut(event);
    this.handleCopy = (event) => this.#writeClipboard(event, { cut: false });
    this.handleCut = (event) => this.#writeClipboard(event, { cut: true });
    this.handlePaste = (event) => this.#readClipboard(event);

    this.#localizeModKeys();

    window.addEventListener('turbo:morph', this.reset);
    window.addEventListener('beforeunload', this.warnBeforeUnload);
    window.addEventListener('keydown', this.handleKey);
    window.addEventListener('copy', this.handleCopy);
    window.addEventListener('cut', this.handleCut);
    window.addEventListener('paste', this.handlePaste);

    this.#reset();
  }

  disconnect() {
    window.removeEventListener('turbo:morph', this.reset);
    window.removeEventListener('beforeunload', this.warnBeforeUnload);
    window.removeEventListener('keydown', this.handleKey);
    window.removeEventListener('copy', this.handleCopy);
    window.removeEventListener('cut', this.handleCut);
    window.removeEventListener('paste', this.handlePaste);
  }

  settleHistory() {
    this.history.settle();
  }

  guardVisit(event) {
    if (this.leaving || !this.#dirty()) return;

    event.preventDefault();

    this.destination = event.detail.url;
    this.leaveModalTarget.showModal();
  }

  confirmLeave() {
    this.leaving = true;

    this.leaveModalTarget.close();
    window.Turbo.visit(this.destination);
  }

  #reset() {
    const hydrated = graph.hydrate(structuredClone(this.stateValue));

    this.saved = JSON.stringify(hydrated);
    this.working = structuredClone(hydrated);
    this.history = new History(this.saved);
    this.selection = new Set();
    this.openStepId = this.working.steps[0]?.id ?? null;
    this.openTriggerId = null;
    this.tab = 'steps';
    this.errors = { steps: {}, triggers: {} };
    this.documentError = null;
    this.counter = 0;
    this.leaving = false;

    this.#json?.cancelPending();

    this.#renderSettings();
    this.#render();
  }

  settingsChanged({ detail: { key } }) {
    this.#renderHeading();
    this.#renderFooter(key);
  }

  stepsChanged({ detail: { key, canvas } }) {
    this.#renderHeading();

    if (canvas) this.#renderCanvas();

    this.#renderFooter(key);
  }

  selectTab({ params: { tab } }) {
    if (tab === this.tab || (this.documentError && tab !== 'json')) return;

    this.#openTab(tab);
  }

  selectTrigger(event) {
    const id = event.currentTarget.closest('[data-trigger-id]').dataset.triggerId;

    if (this.#additive(event)) {
      this.#toggleSelection(id);
      return;
    }

    this.#open(id);
    this.#openTab('steps');
  }

  toggleTrigger({ currentTarget }) {
    const id = currentTarget.closest('[data-trigger-id]').dataset.triggerId;

    this.#open(this.openTriggerId === id ? null : id);

    this.#render();
  }

  insertTrigger({ currentTarget, params: { kind } }) {
    const trigger = { id: this.#nextId(), kind, ...TRIGGER_DEFAULTS, starts: [] };

    this.working.triggers.push(trigger);
    this.#open(trigger.id);

    this.#closeMenu(currentTarget);
    this.#canvas?.focus(trigger.id);
    this.#openTab('steps');
  }

  deleteTrigger({ currentTarget }) {
    this.#removeTrigger(currentTarget.closest('[data-trigger-id]').dataset.triggerId);

    this.#render();
  }

  #removeTrigger(id) {
    graph.removeTrigger(this.working, id);
    this.selection.delete(id);

    if (this.openTriggerId === id) this.openTriggerId = null;
  }

  selectStep(event) {
    const id = event.currentTarget.closest('[data-step-id]').dataset.stepId;

    if (this.#additive(event)) {
      this.#toggleSelection(id);
      return;
    }

    this.#open(id);
    this.#openTab('steps');
  }

  toggleStep({ currentTarget }) {
    const id = currentTarget.closest('[data-step-id]').dataset.stepId;

    this.#open(this.openStepId === id ? null : id);

    this.#render();
  }

  openStepConditions(event) {
    const id = event.currentTarget.closest('[data-step-id]').dataset.stepId;

    this.#open(id);
    this.#openTab('steps');

    requestAnimationFrame(() => this.#steps?.revealConditions(id));
  }

  insertStep({ currentTarget, params: { mode } }) {
    const step = {
      ...STEP_DEFAULTS,
      bundles: [],
      mcpServers: [],
      id: this.#nextId(),
      name: this.labelsValue.newStepNames[mode],
      mode,
      dependsOn: [],
    };

    this.#closeMenu(currentTarget);
    this.working.steps.push(step);
    this.#open(step.id);

    this.#canvas?.focus(step.id);
    this.#openTab('steps');
  }

  deleteStep({ currentTarget }) {
    this.#removeStep(currentTarget.closest('[data-step-id]').dataset.stepId);

    this.#render();
  }

  #removeStep(id) {
    graph.removeStep(this.working, id);
    this.selection.delete(id);

    if (this.openStepId === id) this.openStepId = null;
  }

  nodesMoved() {
    this.#renderFooter();
  }

  selectionChanged({ detail: { selection, settled } }) {
    this.selection = selection;

    if (settled && this.#closeDetached()) this.#render();
  }

  dependencyAdded({ detail: { sourceId, targetId } }) {
    graph.attach(this.working, sourceId, targetId);

    this.#canvas?.refreshWires();
    this.#renderFooter();
  }

  dependencyRemoved({ detail: { childId, parentId } }) {
    graph.unattach(this.working, parentId, childId);

    this.#canvas?.refreshWires();
    this.#renderFooter();
  }

  dependencyMoved({ detail: { parentId, childId, end, nodeId } }) {
    graph.unattach(this.working, parentId, childId);

    const moved =
      end === 'source'
        ? graph.attach(this.working, nodeId, childId)
        : graph.attach(this.working, parentId, nodeId);

    if (!moved) graph.attach(this.working, parentId, childId);

    this.#canvas?.refreshWires();
    this.#renderFooter();
  }

  documentParsed({ detail: { error, workflow } }) {
    this.documentError = error ?? null;

    if (!error) {
      this.working = graph.hydrate(this.#adopt(workflow));

      this.#open(null);

      this.#renderSettings();
      this.#json?.sync(this.working);
    }

    this.#render('json');
  }

  #adopt(workflow) {
    [...workflow.triggers, ...workflow.steps]
      .filter((entity) => entity.id === null)
      .forEach((entity) => {
        entity.id = this.#nextId();
      });

    return workflow;
  }

  discard() {
    this.#adoptState(this.saved);
  }

  #adoptState(state, open = null) {
    this.working = JSON.parse(state);
    this.errors = { steps: {}, triggers: {} };
    this.documentError = null;

    this.#open(this.#step(open) || this.#trigger(open) ? open : null);

    this.#json?.cancelPending();

    if (this.tab === 'json') this.#json?.show(this.working);

    this.#renderSettings();
    this.#render();
  }

  submit(event) {
    this.errors = validate(this.working, {
      messages: this.messagesValue,
      triggerOperators: this.operatorsValue.map((operator) => operator.name),
      stepOperators: this.stepOperatorsValue,
    });

    const faultySteps = Object.keys(this.errors.steps);
    const faultyTriggers = Object.keys(this.errors.triggers);

    if (this.errors.name || this.errors.variables || this.errors.inputs) {
      event.preventDefault();
      this.#openTab('workflow');

      return;
    }

    if (faultyTriggers.length > 0) {
      event.preventDefault();
      this.#open(faultyTriggers[0]);
      this.#openTab('steps');

      return;
    }

    if (faultySteps.length > 0) {
      event.preventDefault();
      this.#open(faultySteps[0]);
      this.#openTab('steps');

      return;
    }

    this.leaving = true;

    this.fieldsTarget.replaceChildren(...serialize(this.working));
  }

  #render(coalesceKey = null) {
    this.#renderTabs();
    this.#renderPanels();
    this.#renderHeading();
    this.#renderCanvas();
    this.#steps?.render(this.#outline());
    this.#settings?.showErrors(this.errors);
    this.#json?.showStatus(this.documentError);
    this.#renderFooter(coalesceKey);
  }

  #openTab(tab) {
    const opening = tab === 'json' && this.tab !== 'json';

    if (tab === 'workflow') this.#renderSettings();

    this.tab = tab;

    this.#render();

    if (opening) this.#json?.show(this.working);
  }

  #renderTabs() {
    for (const tab of this.tabTargets) {
      const active = tab.dataset.workflowEditorTabParam === this.tab;
      const blocked = Boolean(this.documentError) && !active;

      tab.setAttribute('aria-selected', String(active));
      tab.disabled = blocked;
      paint(tab, active, this.tabActiveClasses, this.tabIdleClasses);
    }
  }

  #renderPanels() {
    for (const panel of this.panelTargets) panel.hidden = panel.dataset.panel !== this.tab;
  }

  #renderHeading() {
    this.titleTarget.textContent = this.working.name;
    this.summaryTarget.textContent = [
      pluralize(this.labelsValue.stepCount, this.working.steps.length),
      this.#triggerSummary(),
    ].join(' · ');
  }

  #triggerSummary() {
    const total = this.working.triggers.length;

    if (total === 0) return this.labelsValue.triggerCount.zero;

    return pluralize(this.labelsValue.triggerCount, total);
  }

  #renderSettings() {
    this.#settings?.render(this.working, this.errors);
  }

  #outline() {
    return {
      workflow: this.working,
      errors: this.errors,
      openStepId: this.openStepId,
      openTriggerId: this.openTriggerId,
      catalog: this.#catalog,
    };
  }

  get #catalog() {
    return {
      labels: this.labelsValue,
      messages: this.messagesValue,
      tones: this.tonesValue,
      bundles: this.bundlesValue,
      bundleUrl: this.bundleUrlValue,
      revisions: this.revisionsValue,
      mcpServers: this.mcpServersValue,
      credentials: this.credentialsValue,
      webhooks: this.webhooksValue,
      workflows: this.workflowsValue,
    };
  }

  #renderCanvas() {
    this.#canvas?.render(this.#scene());
  }

  #scene() {
    return {
      workflow: this.working,
      draggable: this.tab !== 'json',
      openId: this.tab === 'steps' ? this.openStepId ?? this.openTriggerId : null,
      selection: this.selection,
      cards: this.#cards(),
      mcpCatalog: new Map(this.mcpServersValue.map((server) => [server.id, server])),
    };
  }

  #cards() {
    const cards = new Map();

    for (const step of this.working.steps) {
      cards.set(step.id, {
        title: stepTitle(step, this.#catalog),
        detail: stepDetail(step, this.#catalog),
        tones: this.tonesValue.mode,
        iconAttribute: 'mode-icon',
        iconValue: step.mode,
      });
    }

    for (const trigger of this.working.triggers) {
      const face = triggerFace(trigger, this.#catalog);

      cards.set(trigger.id, {
        title: face.title,
        detail: face.detail,
        mono: !face.named && trigger.kind === 'cron',
        tones: this.tonesValue.trigger,
        iconAttribute: 'trigger-icon',
        iconValue: trigger.kind,
      });
    }

    return cards;
  }

  #additive(event) {
    return event.metaKey || event.ctrlKey;
  }

  #open(id) {
    const step = this.#step(id);

    this.openStepId = step ? id : null;
    this.openTriggerId = step ? null : id;
    this.selection = id ? new Set([id]) : new Set();
  }

  #closeDetached() {
    const open = this.openStepId ?? this.openTriggerId;

    if (!open || this.selection.has(open)) return false;

    this.openStepId = null;
    this.openTriggerId = null;

    return true;
  }

  get #actions() {
    return {
      selectAll: () => this.#selectAll(),
      clearSelection: () => this.#clearSelection(),
      deleteSelection: () => this.#deleteSelection(),
      undo: () => this.#rewind(this.history.undo()),
      redo: () => this.#rewind(this.history.redo()),
      save: () => this.saveTarget.click(),
      fitView: () => this.#canvas?.fitView(),
      zoomIn: () => this.#canvas?.zoomIn(),
      zoomOut: () => this.#canvas?.zoomOut(),
    };
  }

  #localizeModKeys() {
    if (/Mac|iPhone|iPad/.test(navigator.userAgent)) return;

    for (const key of this.element.querySelectorAll('[data-role="mod-key"]')) {
      key.textContent = 'Ctrl';
    }
  }

  #shortcut(event) {
    if (typing(event.target) || document.querySelector('dialog[open]')) return;

    const pressed = chord(event);
    const shortcut = this.shortcutsValue.find((candidate) => candidate.chords.includes(pressed));
    const action = shortcut && this.#actions[shortcut.action];

    if (!action) return;

    event.preventDefault();

    action();
  }

  #writeClipboard(event, { cut }) {
    if (typing(event.target) || document.querySelector('dialog[open]')) return;
    if (window.getSelection()?.toString()) return;

    const payload = copyPayload(this.working, this.selection);

    if (!payload) return;

    event.preventDefault();
    event.clipboardData?.setData('text/plain', JSON.stringify(payload, null, 2));

    if (cut) this.#deleteSelection();
  }

  #readClipboard(event) {
    if (typing(event.target) || document.querySelector('dialog[open]')) return;

    const payload = parsePayload(event.clipboardData?.getData('text/plain'));

    if (!payload) return;

    event.preventDefault();
    this.#insertNodes(payload);
  }

  #insertNodes(payload) {
    const { steps, triggers } = pasteNodes(this.working, payload, () => this.#nextId());

    this.selection = new Set([...steps, ...triggers].map((node) => node.id));
    this.openStepId = steps[0]?.id ?? null;
    this.openTriggerId = steps.length > 0 ? null : triggers[0]?.id ?? null;

    this.#render();
    this.#canvas?.focus(this.openStepId ?? this.openTriggerId);
  }

  #deleteSelection() {
    if (this.selection.size === 0) return;

    for (const id of [...this.selection]) {
      if (this.#step(id)) this.#removeStep(id);
      else this.#removeTrigger(id);
    }

    this.#render();
  }

  #selectAll() {
    const ids = [...this.working.triggers, ...this.working.steps].map((node) => node.id);

    if (ids.length === 0) return;

    this.selection.clear();
    ids.forEach((id) => this.selection.add(id));

    this.#refreshSelection();
  }

  #clearSelection() {
    if (this.selection.size === 0 && !this.openStepId && !this.openTriggerId) return;

    this.#open(null);
    this.#render();
  }

  #toggleSelection(id) {
    if (this.selection.has(id)) {
      this.selection.delete(id);
    } else {
      this.selection.add(id);
    }

    this.#refreshSelection();
  }

  #refreshSelection() {
    if (this.#closeDetached()) {
      this.#render();
      return;
    }

    this.#canvas?.paintSelection();
  }

  #dirty() {
    return JSON.stringify(this.working) !== this.saved;
  }

  #rewind(state) {
    if (!state) return;

    this.#adoptState(state, this.openStepId ?? this.openTriggerId);
  }

  #renderFooter(coalesceKey = null) {
    this.history.record(JSON.stringify(this.working), coalesceKey);

    const dirty = this.#dirty();

    this.dirtyTarget.hidden = !dirty;
    this.discardTarget.hidden = !dirty;
    this.saveTarget.disabled = !dirty || Boolean(this.documentError);

    this.dispatch('changed', { target: window, detail: { dirty } });
  }

  #trigger(id) {
    return graph.findTrigger(this.working, id);
  }

  #step(id) {
    return graph.findStep(this.working, id);
  }

  #closeMenu(node) {
    const menu = node.closest('[data-controller~="action-menu"]');

    if (menu) this.application.getControllerForElementAndIdentifier(menu, 'action-menu')?.close();
  }

  #nextId() {
    this.counter += 1;

    return `new-${this.counter}`;
  }
}
